import React from 'react';
import { LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined, AudioOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import { SceneState } from '../../types/heygen';
import { estimateDuration, formatDuration } from '../../utils/text-stats';
import { C } from '../../constants/design-tokens';
import { useResponsiveSizes } from '../../../../hooks/use-responsive-sizes';

type SceneProcessingStatus = 'pending' | 'video_processing' | 'completed' | 'failed' | undefined;

interface SceneListItemProps {
  scene: SceneState;
  index: number;
  isActive: boolean;
  processingStatus?: SceneProcessingStatus;
  onSelect: () => void;
}

const StatusIndicator: React.FC<{ status: SceneProcessingStatus }> = ({ status }) => {
  if (!status || status === 'pending') return null;
  if (status === 'video_processing') return <LoadingOutlined style={{ fontSize: 14, color: C.warning }} />;
  if (status === 'completed') return <CheckCircleOutlined style={{ fontSize: 14, color: C.success }} />;
  if (status === 'failed') return <CloseCircleOutlined style={{ fontSize: 14, color: C.danger }} />;
  return null;
};

export const SceneListItem: React.FC<SceneListItemProps> = ({ scene, index, isActive, processingStatus, onSelect }) => {
  const { spacing, fontSize } = useResponsiveSizes();
  const [hovered, setHovered] = React.useState(false);
  const duration = estimateDuration(scene.script);
  const scriptPreview = scene.script.trim() || 'Sin guion...';

  return (
    <div
      onClick={onSelect}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: spacing.sm,
        padding: `${spacing.sm}px ${spacing.sm}px`,
        marginBottom: spacing.xs,
        borderRadius: 12,
        cursor: 'pointer',
        background: isActive ? C.accentSurface : hovered ? C.surface : 'transparent',
        border: `1px solid ${isActive ? C.accent : hovered ? C.borderHi : C.border}`,
        transition: 'all 0.15s ease'
      }}
    >
      {/* Scene info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span
              style={{
                fontSize: fontSize.sm,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.4,
                color: isActive ? C.accent : C.textSecondary
              }}
            >
              Escena {String(index + 1).padStart(2, '0')}
            </span>
            {scene.onlyVoice && (
              <Tooltip title='Solo voz' placement='top'>
                <AudioOutlined
                  style={{
                    fontSize: fontSize.sm,
                    color: isActive ? C.accent : C.textSecondary
                  }}
                />
              </Tooltip>
            )}
          </div>
          <span
            style={{
              fontSize: fontSize.xs,
              color: C.textMuted,
              fontVariantNumeric: 'tabular-nums'
            }}
          >
            {formatDuration(duration)}
          </span>
        </div>
        <div
          style={{
            fontSize: fontSize.sm,
            fontWeight: 500,
            color: scene.script.trim() ? C.textPrimary : C.textMuted,
            fontStyle: scene.script.trim() ? 'normal' : 'italic',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {scriptPreview}
        </div>
      </div>
      {processingStatus && processingStatus !== 'pending' && (
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 6,
            flexShrink: 0,
            backgroundColor: C.surface,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <StatusIndicator status={processingStatus} />
        </div>
      )}
    </div>
  );
};
